import includes from 'lodash/includes';
import split from 'lodash/split';
import startsWith from 'lodash/startsWith';

/**
 * The in-app login-method chooser.
 *
 * Universal Login cannot offer a password-vs-code choice when a tenant has
 * both a database connection and a passwordless connection enabled: the app
 * has to pick one via the `connection` authorize parameter. So the choice is
 * made on our own /sign-in page, and the last choice is remembered per
 * browser so returning users see their usual method as the primary button.
 */
export type LoginMethod = 'code' | 'password';

export const SIGN_IN_PATH = '/sign-in';
export const SIGN_UP_PATH = '/sign-up';

export const DEFAULT_LOGIN_METHOD: LoginMethod = 'code';

// Auth0 connection names. The passwordless email connection is literally
// named `email` on both tenants; the database connection is named after the
// tenant (`local-oilytics` locally, `development-oilytics` in prod), hence
// the env var.
const PASSWORDLESS_CONNECTION = 'email';
const DATABASE_CONNECTION: string = import.meta.env.VITE_AUTH_DB_CONNECTION;

const STORAGE_KEY = 'login-method';

// Paths that must never become a returnTo: bouncing back to them after login
// would either loop or land the user on a page meant for signed-out visitors.
const AUTH_PATHS = [SIGN_IN_PATH, SIGN_UP_PATH, '/callback'];

export function connectionForMethod (method: LoginMethod): string {
  return method === 'password' ? DATABASE_CONNECTION : PASSWORDLESS_CONNECTION;
}

export function getRememberedLoginMethod (): LoginMethod | null {
  const stored = localStorage.getItem(STORAGE_KEY);
  return stored === 'code' || stored === 'password' ? stored : null;
}

export function rememberLoginMethod (method: LoginMethod) {
  localStorage.setItem(STORAGE_KEY, method);
}

/** Builds the /sign-in URL that returns to `location` after login. */
export function signInPath (location: { pathname: string; search: string }): string {
  if (includes(AUTH_PATHS, location.pathname)) return SIGN_IN_PATH;
  const returnTo = `${location.pathname}${location.search}`;
  return `${SIGN_IN_PATH}?returnTo=${encodeURIComponent(returnTo)}`;
}

/** Builds the /sign-up URL that returns to `location` after signup. */
export function signUpPath (location: { pathname: string; search: string }): string {
  if (includes(AUTH_PATHS, location.pathname)) return SIGN_UP_PATH;
  const returnTo = `${location.pathname}${location.search}`;
  return `${SIGN_UP_PATH}?returnTo=${encodeURIComponent(returnTo)}`;
}

/**
 * Only same-origin relative paths are honoured as a returnTo. Anything else
 * (absolute URLs, protocol-relative `//evil`, auth pages) falls back to `/`,
 * so the query param can't be used as an open redirect.
 */
export function safeReturnTo (raw: string | null): string {
  if (!raw || !startsWith(raw, '/') || startsWith(raw, '//')) return '/';
  const [pathname] = split(raw, '?');
  if (includes(AUTH_PATHS, pathname)) return '/';
  return raw;
}
